import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { ProfileService, StaticContentService } from '@mwe/services';
import { TariffSelectionCardData, TextNodeType } from '@mwe/models';
import { TariffSelectionCardLogic } from '../services/tariff-selection-card.logic';
import { FeatureToggleEnum, ServiceStateEnum } from '@mwe/constants';
import { HeadlineService } from '../../headline/headline.service';
import { TariffSelectionStore } from '../services/tariff-selection.store';
import { BasicCardComponent } from '../../cards/basic-card/basic-card.component';
import { NgClass } from '@angular/common';
import { HeadlineComponent } from '../../headline/headline.component';
import { ExpanderComponent } from '../../expander/expander.component';
import { TranslatePipe } from '@ngx-translate/core';
import { CorrelatedCompanyReferencePipe } from '@mwe/ui/pipes';
import { isAllesSicherOptionAlreadyApplied } from '@mwe/utils';

@Component({
  selector: 'mwe-tariff-card-additional-options',
  templateUrl: './tariff-card-additional-options.component.html',
  providers: [HeadlineService],
  imports: [BasicCardComponent, NgClass, HeadlineComponent, ExpanderComponent, TranslatePipe, CorrelatedCompanyReferencePipe],
})
export class TariffCardAdditionalOptionsComponent implements OnInit {
  // tariff comes from signal() do not manipulate it!!!! use TariffSelectionStore!!!!!
  tariff = input.required<TariffSelectionCardData>();
  isReadOnly = input<boolean>(false);
  headlineReference = input<TextNodeType>('h4');
  isKundeWerden = input<boolean>(false);

  infoText = signal<string>('');
  title = signal<string>('');
  isSelected = signal<boolean>(false);
  additionalOptionGroupName = computed<string>(() => this.store.additionalOptionGroupName());
  isAllesSicherAlreadyApplied = computed<boolean>(() =>
    isAllesSicherOptionAlreadyApplied(this.tariff().tariffOptionsSap, this.additionalOptionGroupName()),
  );
  isCheckBoxDisabled = computed(
    () => this.tariff()?.state !== ServiceStateEnum.SUCCESS || this.isReadOnly() || this.isAllesSicherAlreadyApplied(),
  );
  headlineService = inject(HeadlineService);
  private store = inject(TariffSelectionStore);
  private staticContentService = inject(StaticContentService);
  private cardLogic = inject(TariffSelectionCardLogic);
  private profileService = inject(ProfileService);

  async ngOnInit() {
    this.headlineService.setReferenceHeadline(this.headlineReference());
    const {
      tarifwelt: {
        optionen: { zusatzoptionen },
      },
    } = await this.staticContentService.getInfoTextList();
    this.infoText.set(zusatzoptionen?.find(option => option.code === 'art-label')?.description ?? '');
    this.setTitle();
    this.getSelection();
  }

  async onCheckboxChange() {
    this.isSelected.update(old => !old); // toggle select value
    // default is always "thanks but no", so default: true -> not checked and vice versa
    const newOption = this.tariff().tariffOptionsSap.find(
      o => o.art === this.additionalOptionGroupName() && o.default !== this.isSelected(),
    );
    await this.cardLogic.handleTariffOptionChange(this.tariff(), newOption);
  }

  private setTitle() {
    let optionId: string;
    if (this.isReadOnly() || (this.isCheckBoxDisabled() && this.isAllesSicherAlreadyApplied())) {
      optionId = this.tariff().userSelection.find(t => t.art === this.additionalOptionGroupName())?.option;
    } else {
      optionId = this.tariff().tariffOptionsSap.find(t => t.art === this.additionalOptionGroupName() && !t?.default)?.option;
    }
    this.title.set(this.getLabel(optionId));
  }

  private getLabel(optionId: string): string {
    if (this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.ALLES_SICHER_READ_LABEL_FROM_SAP_ENABLED)) {
      return this.tariff().tariffOptionsSap.find(t => t?.option === optionId)?.optionLabel || '';
    }
    return this.tariff().priceIndication.tarif.tarifOptionen.find(t => t?.option === optionId)?.optionLabel || '';
  }

  private getSelection() {
    // get the preselected additional option
    const selectedUserOption = this.tariff().userSelection.find(o => o.art === this.additionalOptionGroupName());
    // default is always "thanks but no", so default: true -> not checked and vice versa unless user already have this option applied
    this.isSelected.set(!selectedUserOption?.default || (this.isCheckBoxDisabled() && this.isAllesSicherAlreadyApplied()));
  }
}
